import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, Trophy } from 'lucide-react';
import Confetti from 'react-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DrawExecution() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [winners, setWinners] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [drawExecuted, setDrawExecuted] = useState(false);

  useEffect(() => {
    fetchEvent();
    fetchWinners();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
    }
  };

  const fetchWinners = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/winners`);
      if (response.data.draw_executed) {
        setWinners(response.data.winners);
        setDrawExecuted(true);
      }
    } catch (error) {
      console.error('Error fetching winners:', error);
    }
  };

  const handleExecuteDraw = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/events/${eventId}/draw`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWinners(response.data.winners);
      setShowConfetti(true);
      setDrawExecuted(true);
      toast.success('Draw executed successfully!');
      setTimeout(() => setShowConfetti(false), 8000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute draw');
    }
  };

  if (!event) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-outfit text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-6 pb-12" data-testid="draw-execution-page">
      {showConfetti && <Confetti recycle={false} numberOfPieces={800} />}
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={() => navigate('/business/dashboard')}
          variant="ghost"
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="font-playfair font-bold text-4xl text-primary mb-2">{event.event_name}</h1>
          <p className="font-outfit text-gray-600">Lucky Draw Management</p>
        </div>

        {!drawExecuted ? (
          <Card className="p-12 text-center">
            <Sparkles className="w-16 h-16 mx-auto mb-6 text-amber-600" />
            <h2 className="font-playfair font-bold text-3xl text-primary mb-4">Ready to Draw Winners?</h2>
            <p className="font-outfit text-gray-600 mb-8 max-w-md mx-auto">
              This will randomly select winners from all approved coupons using a secure cryptographic algorithm.
            </p>
            <Button
              onClick={handleExecuteDraw}
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-lg px-8 py-6"
              data-testid="execute-draw-button"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Execute Lucky Draw
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card className="p-8 text-center bg-gradient-to-r from-amber-50 to-yellow-50">
              <Trophy className="w-20 h-20 mx-auto mb-4 text-amber-600" />
              <h2 className="font-playfair font-bold text-3xl text-primary mb-2">Winners Announced!</h2>
              <p className="font-outfit text-gray-600">Congratulations to all winners</p>
            </Card>

            {winners.map((winner, idx) => (
              <Card key={idx} className="p-6" data-testid={`winner-${idx}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-playfair font-bold text-2xl text-amber-600 mb-1">
                      {winner.prize_name}
                    </h3>
                    <p className="font-outfit text-gray-600 mb-2">${winner.prize_amount} - {winner.prize_description}</p>
                    <p className="font-outfit text-sm text-gray-500">
                      Winner: {winner.customer_name || winner.user_id} | Coupon: {winner.coupon_id}
                    </p>
                  </div>
                  <Trophy className="w-12 h-12 text-amber-600" />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}